import type {
  SurfaceFindingDto,
  ExternalNetworkSignalDto,
} from '@terra/shared-contracts-external-network';
import {
  SurfaceFindingsListResponseSchema,
  ExternalNetworkSignalListResponseSchema,
} from '@terra/shared-contracts-external-network';
import type { InternalNetworkSignalDto } from '@terra/shared-contracts-internal-network';
import { InternalNetworkSignalListResponseSchema } from '@terra/shared-contracts-internal-network';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { z } from 'zod';
import type { SignalsCountByApplicationIdResponseDto } from '@terra/shared-contracts-signals';
import type { findingsClient } from '@terra/shared-rest-clients-findings';
import type {
  ConfirmedVulnerabilityFindingStateType,
  FindingStatusType,
  FindingSeverityType,
  SignalCategoryType,
  SignalOriginType,
  SignalStageType,
  SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import type { BulkUpdateFindingsPayloadDto } from '@terra/shared-contracts-findings';
import {
  LifecycleStateFilterSchema,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';

export type FindingsBulkUpdate = BulkUpdateFindingsPayloadDto['update'];
type GetFindingsListEnrichedResponse = Awaited<
  ReturnType<typeof findingsClient.listFindingsEnriched>
>;

export type FindingByApplicationDto = Extract<
  GetFindingsListEnrichedResponse,
  { status: 200 }
>['body']['data'][number];
export type FindingsBulkFilter = {
  endpointId?: string;
  searchTerm?: string;
  severity?: FindingSeverityType[];
  status?: FindingStatusType[];
  category?: SignalCategoryType[];
  isPublished?: boolean;
  wasAutoPublished?: boolean;
  hasPrevention?: boolean;
  hasPendingSeverityChangeRequest?: boolean;
  createdStartDate?: Date;
  createdEndDate?: Date;
  discoveryStartDate?: Date;
  discoveryEndDate?: Date;
  lifecycleState?: LifecycleStateFilterDto;
};
export type FindingsBulkFilterPayload = {
  applicationId: string;
  filter: FindingsBulkFilter;
  excludedIds: string[];
};
export type SurfaceTargetId = string;

export type SurfaceFindingsListParams = {
  targetType: TargetTypeType;
  surfaceId: SurfaceTargetId;
  skip?: number;
  limit?: number;
  severity?: string;
  status?: string;
  signalId?: string;
};
export type SurfaceFindingsListResult = {
  data: ReadonlyArray<SurfaceFindingDto>;
  total: number;
};
export const toSurfaceFindingsListResult = (
  result: z.input<typeof SurfaceFindingsListResponseSchema>,
): SurfaceFindingsListResult => SurfaceFindingsListResponseSchema.parse(result);
export type SurfaceSignalConfidenceFilter =
  | 'HIGHEST'
  | 'HIGH'
  | 'MEDIUM'
  | 'LOW'
  | 'LOWEST'
  | 'ALL'
  | Array<'HIGHEST' | 'HIGH' | 'MEDIUM' | 'LOW' | 'LOWEST' | 'ALL'>;
export type SurfaceSignalsListParams = {
  targetType: TargetTypeType;
  surfaceId: SurfaceTargetId;
  skip?: number;
  limit?: number;
  category?: string[];
  search?: string;
  stage?: SignalStageType[];
  status?: SignalStatusType[];
  confirmedVulnerabilityFindingState?: ConfirmedVulnerabilityFindingStateType[];
  confidence?: SurfaceSignalConfidenceFilter;
  origin?: SignalOriginType[];
  source?: string[];
  assigneeId?: string[];
  hasExecution?: boolean;
  hasFinding?: boolean;
  hasSecurityTestCase?: boolean;
  createdStartDate?: Date;
  createdEndDate?: Date;
  testedStartDate?: Date;
  testedEndDate?: Date;
  rejectedOnly?: boolean;
  includeRejected?: boolean;
  entityId?: string;
  entityKind?: string;
  lifecycleState?: LifecycleStateFilterDto;
};
export function resolveSurfaceSignalEntityQuery({
  surfaceAssetEntityId,
  surfaceAssetKind,
  targetType,
}: {
  surfaceAssetEntityId?: string;
  surfaceAssetKind?: string;
  targetType?: TargetTypeType;
}): Pick<SurfaceSignalsListParams, 'entityId' | 'entityKind'> {
  if (!surfaceAssetEntityId) {
    return {};
  }

  if (targetType === 'EXTERNAL_NETWORK') {
    const entityKind =
      surfaceAssetKind === 'DOMAIN' ||
      surfaceAssetKind === 'HOST' ||
      surfaceAssetKind === 'NETWORK_SERVICE' ||
      surfaceAssetKind === 'SOFTWARE'
        ? surfaceAssetKind
        : undefined;

    return {
      entityId: surfaceAssetEntityId,
      ...(entityKind ? { entityKind } : {}),
    };
  }

  if (targetType === 'INTERNAL_NETWORK') {
    return {
      entityId: surfaceAssetEntityId,
      ...(surfaceAssetKind ? { entityKind: surfaceAssetKind } : {}),
    };
  }

  return {};
}

export type SurfaceSignalsListResult = {
  data: Array<ExternalNetworkSignalDto | InternalNetworkSignalDto>;
  total: number;
};
export const toInternalSurfaceSignalsListResult = (
  result: z.input<typeof InternalNetworkSignalListResponseSchema>,
): SurfaceSignalsListResult => {
  const parsed = InternalNetworkSignalListResponseSchema.parse(result);

  return {
    total: parsed.total,
    data: parsed.data,
  };
};

export const toExternalSurfaceSignalsListResult = (
  result: z.input<typeof ExternalNetworkSignalListResponseSchema>,
): SurfaceSignalsListResult => {
  const parsed = ExternalNetworkSignalListResponseSchema.parse(result);

  return {
    total: parsed.total,
    data: parsed.data,
  };
};

export type TargetScopeParams = {
  targetId: string;
  targetType: TargetTypeType;
};
export type TargetScopedSignalCountsResult = {
  total: number;
  counts: { ALL: number } & Record<string, number>;
  byStage?: Record<SignalStageType, number>;
};
export const EMPTY_TARGET_SIGNAL_COUNTS: TargetScopedSignalCountsResult = {
  total: 0,
  counts: {
    ALL: 0,
    HOT: 0,
    COLD: 0,
    PASSED: 0,
    EXPLOITED: 0,
    DUPLICATE: 0,
    BLOCKED: 0,
  },
};
export const mapAppSignalCountsToLegacy = (
  result: SignalsCountByApplicationIdResponseDto,
): TargetScopedSignalCountsResult => {
  const stageCounts = result.counts;
  const statusCounts = result.statusCounts;

  return {
    total: result.total,
    counts: {
      ALL: stageCounts.ALL,
      HOT: statusCounts.POTENTIAL_VULNERABILITY,
      COLD:
        stageCounts.NEW + stageCounts.IN_TESTING + statusCounts.UNCLASSIFIED,
      PASSED: statusCounts.DISPROVEN_VULNERABILITY,
      EXPLOITED: statusCounts.CONFIRMED_VULNERABILITY,
      DUPLICATE: statusCounts.DUPLICATE,
      BLOCKED: statusCounts.BLOCKED,
    },
    byStage: {
      NEW: stageCounts.NEW,
      IN_TESTING: stageCounts.IN_TESTING,
      NEEDS_REVIEW: stageCounts.NEEDS_REVIEW,
      TESTED: stageCounts.TESTED,
    },
  };
};

export const toSurfaceSignalListQueryFromPortalFilters = (
  filters?: Record<string, unknown>,
): Omit<
  SurfaceSignalsListParams,
  'targetType' | 'surfaceId' | 'skip' | 'limit' | 'entityId' | 'entityKind'
> => {
  const description =
    typeof filters?.description === 'string' ? filters.description.trim() : '';
  const parsedLifecycleState = LifecycleStateFilterSchema.safeParse(
    filters?.lifecycleState,
  );

  return {
    category: resolveSurfaceCategoryFromPortalFilters(filters?.category),
    search: description.length > 0 ? description : undefined,
    stage: Array.isArray(filters?.stage)
      ? (filters.stage as SignalStageType[])
      : undefined,
    status: Array.isArray(filters?.status)
      ? (filters.status as SignalStatusType[])
      : undefined,
    confirmedVulnerabilityFindingState: Array.isArray(
      filters?.confirmedVulnerabilityFindingState,
    )
      ? (filters.confirmedVulnerabilityFindingState as ConfirmedVulnerabilityFindingStateType[])
      : undefined,
    confidence: Array.isArray(filters?.confidence)
      ? (filters.confidence as SurfaceSignalConfidenceFilter)
      : undefined,
    origin: Array.isArray(filters?.origin)
      ? (filters.origin as SignalOriginType[])
      : undefined,
    source: Array.isArray(filters?.source)
      ? (filters.source as string[])
      : undefined,
    assigneeId: Array.isArray(filters?.assigneeId)
      ? (filters.assigneeId as string[])
      : undefined,
    hasExecution:
      typeof filters?.hasExecution === 'boolean'
        ? filters.hasExecution
        : undefined,
    hasFinding:
      typeof filters?.hasFinding === 'boolean' ? filters.hasFinding : undefined,
    hasSecurityTestCase:
      typeof filters?.hasSecurityTestCase === 'boolean'
        ? filters.hasSecurityTestCase
        : undefined,
    createdStartDate:
      filters?.createdStartDate instanceof Date
        ? filters.createdStartDate
        : undefined,
    createdEndDate:
      filters?.createdEndDate instanceof Date
        ? filters.createdEndDate
        : undefined,
    testedStartDate:
      filters?.testedStartDate instanceof Date
        ? filters.testedStartDate
        : undefined,
    testedEndDate:
      filters?.testedEndDate instanceof Date
        ? filters.testedEndDate
        : undefined,
    rejectedOnly:
      typeof filters?.rejectedOnly === 'boolean'
        ? filters.rejectedOnly
        : undefined,
    includeRejected:
      typeof filters?.includeRejected === 'boolean'
        ? filters.includeRejected
        : undefined,
    lifecycleState: parsedLifecycleState.success
      ? parsedLifecycleState.data
      : undefined,
  };
};

export const resolveSurfaceCategoryFromPortalFilters = (
  category: unknown,
): string[] | undefined => {
  if (typeof category === 'string') {
    return category === 'ALL' ? undefined : [category];
  }

  if (Array.isArray(category) && category.length > 0) {
    const categories = category.filter(
      (entry): entry is string => typeof entry === 'string',
    );

    return categories.length > 0 ? categories : undefined;
  }

  return undefined;
};

export const toBulkFilterBody = ({
  payload,
  includeUnpublished = true,
}: {
  payload: FindingsBulkFilterPayload;
  includeUnpublished?: boolean;
}) => {
  const { lifecycleState, ...filter } = payload.filter;

  return {
    applicationId: payload.applicationId,
    filter,
    excludedIds: payload.excludedIds,
    includeUnpublished,
    ...(lifecycleState ? { lifecycleState } : {}),
  };
};
